import React, { useState } from "react";
import { injectIntl } from "react-intl";
import messages from "./Matches.intl";
import { styled, makeStyles } from "@material-ui/styles";
import { Theme, Fab, Tab, Tabs } from "@material-ui/core";
import MatchesList from "./MatchesList";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client";
import Loader from "../loader";
import AddIcon from "@material-ui/icons/Add";
import useIsManager from "../../hooks/useIsManager";
import { ActionLink } from "../atoms";
import { IStyledArguments } from "../../types/styled-arguments";

export const MATCHES_QUERY = gql`
  query GetMatches($historical: Boolean) {
    allMatches(historical: $historical) {
      id
      date
      location
      time
      status
      homeTeam {
        id
        name
      }
      isHomeTeamLocal
      isFriendly
      awayTeam
      score {
        home
        away
      }
      playersSelected {
        id
        name
      }
      playersConfirmed {
        id
        name
      }
      scorerList {
        player {
          id
          name
          surname
          avatarUrl
        }
        goals
      }
      ownGoals
      videos {
        id
        title
        url
        thumbnail
      }
    }
    currentUser {
      id
      role
    }
  }
`;

const FooterContainer = styled("div")<Theme>(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  padding: theme.spacing(2),
  height: 90,
  width: "auto",
  right: 0,
  position: "fixed",
  bottom: 70,
}));

const useLoaderStyles = makeStyles((theme: Theme) => ({
  root: {
    minHeight: `calc(100vh - ${theme.layout.headerHeight -
      theme.layout.tabsHeight}px )`, // compensate for list filter bar height
  },
}));

const Main = styled("main")<Theme>(() => ({
  display: "block", // IE
  // minHeight: `calc(100vh - ${theme.layout.headerHeight + 49}px )`,
  // background: theme.palette.common.white
}));

const SeasonTabsWrapper = styled("div")(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(0, 2),
  borderBottom: `1px solid ${theme.palette.extra.borderColor}`,
  background: theme.palette.background.paper,
}));

const PageTitle = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 700,
  fontSize: "1.4rem",
  color: theme.palette.text.primary,
  padding: theme.spacing(2.5, 2, 1.5),
  background: theme.palette.background.paper,
}));

const SeasonTabs = styled(Tabs)(({ theme }: IStyledArguments) => ({
  minHeight: 44,
  "& .MuiTabs-indicator": {
    backgroundColor: theme.palette.primary.main,
    height: 3,
    borderRadius: "3px 3px 0 0",
  },
  "& .MuiTab-root": {
    minHeight: 44,
    minWidth: "auto",
    padding: theme.spacing(1, 0.5),
    marginRight: theme.spacing(3),
    fontWeight: 600,
    fontSize: "0.95rem",
    textTransform: "none",
    color: theme.palette.text.secondary,
    "&.Mui-selected": {
      color: theme.palette.text.primary,
      fontWeight: 700,
    },
  },
}));

const Matches = injectIntl(({ intl }) => {
  const [showHistorical, setShowHistorical] = useState(false);
  const { data, loading } = useQuery<any>(MATCHES_QUERY, {
    variables: { historical: showHistorical },
    // El fetchPolicy por defecto (cache-first) mostraba la lista cacheada de
    // antes de crear un partido nuevo al volver a esta pantalla, sin pasar
    // por la red — cache-and-network sigue mostrando la caché al instante
    // pero siempre revalida contra el servidor al montar.
    fetchPolicy: "cache-and-network",
  });
  const loaderClasses = useLoaderStyles({});

  return (
    <>
      <PageTitle>{intl.formatMessage(messages.title)}</PageTitle>
      <SeasonTabsWrapper>
        <SeasonTabs
          value={showHistorical ? 1 : 0}
          onChange={(_event, newValue) => setShowHistorical(newValue === 1)}
        >
          <Tab label={intl.formatMessage(messages.currentSeason)} />
          <Tab label={intl.formatMessage(messages.pastSeasons)} />
        </SeasonTabs>
      </SeasonTabsWrapper>
      <Main>
        <Loader
          className={loaderClasses.root}
          errorMessage={intl.formatMessage(messages.loadError)}
          loading={loading}
          // error={error}
          data={data}
        >
          {({ allMatches, currentUser }) => (
            <>
              <MatchesList
                items={allMatches}
                currentUser={currentUser}
                emptyMessage={
                  showHistorical
                    ? intl.formatMessage(messages.noPastMatches)
                    : intl.formatMessage(messages.noMatches)
                }
              />
              <FooterContainer>
                {useIsManager(currentUser.role) && (
                  <ActionLink to="/matches/newmatch">
                    <Fab
                      aria-label="add match"
                      color="secondary"
                      style={{ zIndex: 1200 }}
                    >
                      <AddIcon />
                    </Fab>
                  </ActionLink>
                )}
              </FooterContainer>
            </>
          )}
        </Loader>
      </Main>
    </>
  );
});

export default Matches;
